import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * Owns the Flow 3 cart-page interactions while Shopify remains authoritative
 * for line keys, inventory, prices, discounts, totals, and checkout state.
 */
class AtharCart extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
    this.querySelector('[data-athar-checkout-form]')?.addEventListener('submit', this.#handleCheckout);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    this.querySelector('[data-athar-checkout-form]')?.removeEventListener('submit', this.#handleCheckout);
  }

  #handleClick = (event) => {
    const control = event.target.closest('[data-athar-cart-action]');
    if (!(control instanceof HTMLButtonElement) || this.hasAttribute('aria-busy')) return;

    const row = control.closest('[data-athar-cart-line]');
    const input = row?.querySelector('[data-athar-cart-quantity]');
    if (!(row instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;

    const action = control.dataset.atharCartAction;
    const current = Number(input.value) || 0;
    const nextQuantity = action === 'remove' ? 0 : action === 'increase' ? current + 1 : Math.max(0, current - 1);
    this.#changeLine(row.dataset.key || '', nextQuantity, action === 'remove' || nextQuantity === 0 ? 'remove' : 'update');
  };

  #handleChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-athar-cart-quantity]')) return;
    const row = input.closest('[data-athar-cart-line]');
    if (!(row instanceof HTMLElement)) return;
    const quantity = Math.max(0, Math.floor(Number(input.value) || 0));
    this.#changeLine(row.dataset.key || '', quantity, quantity === 0 ? 'remove' : 'update');
  };

  /** Disable all mutating controls so repeated requests cannot race each other. */
  #setBusy(isBusy) {
    this.toggleAttribute('aria-busy', isBusy);
    this.querySelectorAll('[data-athar-cart-action], [data-athar-cart-quantity]').forEach((control) => {
      control.disabled = isBusy;
    });
  }

  #setStatus(message = '') {
    const status = this.querySelector('[data-athar-cart-status]');
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
  }

  /**
   * Update by Shopify cart line key, not product ID, so customized copies and
   * sibling variants remain independent lines.
   */
  async #changeLine(key, quantity, action) {
    if (!key) return;
    this.#setBusy(true);
    this.#setStatus(quantity === 0 ? 'Removing item…' : 'Updating cart…');

    try {
      const response = await fetch(Theme.routes.cart_change_url || '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: [this.dataset.sectionId], sections_url: window.location.pathname }),
      });
      const cart = await response.json();
      if (!response.ok || cart.status) throw new Error(cart.description || cart.message || 'Shopify could not update this cart line.');

      const updatePromise = Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: false, items: cart.items, sections: cart.sections, source: 'athar-cart' },
      });
      this.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines: [{ id: key, quantity }], promise: updatePromise }));

      const sectionHtml = cart.sections?.[this.dataset.sectionId];
      if (!sectionHtml) return window.location.reload();
      const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const nextCart = parsed.querySelector('athar-cart');
      if (!(nextCart instanceof HTMLElement)) return window.location.reload();
      this.replaceWith(nextCart);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'A network error prevented the cart update. Please retry.';
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'SERVICE_UNAVAILABLE' }));
      this.#setStatus(message);
      this.#setBusy(false);
    }
  }

  #handleCheckout = (event) => {
    const form = event.currentTarget;
    if (!(form instanceof HTMLFormElement)) return;
    const button = form.querySelector('[data-athar-checkout]');
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      const label = button.querySelector('span');
      if (label) label.textContent = 'Opening checkout…';
    }
  };
}

if (!customElements.get('athar-cart')) customElements.define('athar-cart', AtharCart);
